// Thin typed client for the custom /api/dpp/* routes (catalog, dpp,
// identifier, documents, tenancy, publishing). PocketBase's own collection
// API is used directly via the SDK (see pb.ts) where collection rules permit
// it (tenancy_users, dpp_versions, tenancy_memberships, audit_log).
import { pb, PB_URL } from "./pb";

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (pb.authStore.token) {
    headers["Authorization"] = pb.authStore.token;
  }

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const res = await fetch(`${PB_URL}/api/dpp${path}`, {
    method,
    headers,
    body: payload,
  });

  if (!res.ok) {
    let message = res.statusText || `request failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      // response body wasn't JSON — keep the status text
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

/** Fetches a binary resource (e.g. a QR PNG) as an object URL the caller must revoke. */
export async function fetchObjectURL(path: string): Promise<string> {
  const headers: Record<string, string> = {};
  if (pb.authStore.token) {
    headers["Authorization"] = pb.authStore.token;
  }
  const res = await fetch(`${PB_URL}/api/dpp${path}`, { headers });
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText || "request failed");
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
